import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Skeleton } from 'components/ui/skeleton';
import { Badge } from 'components/ui/badge';
import { MapTrifold } from '@phosphor-icons/react';
import { formatCurrency } from 'shared/utils/currency';
import { AcquisitionRegionBucket } from '../types/statistics.types';
import { MetricInfo } from './MetricInfo';

interface Props {
  data?: AcquisitionRegionBucket[];
  loading: boolean;
}

interface RegionRow extends AcquisitionRegionBucket {
  conversion: number;
  avgVolumePerDossier: number;
}

export const RegionPerformanceTable = ({ data, loading }: Props) => {
  const rows: RegionRow[] = useMemo(() => {
    if (!data) return [];
    return [...data]
      .map((r) => {
        const decided = r.deals + r.rejected;
        const conversion = decided > 0 ? r.deals / decided : 0;
        const avgVolumePerDossier = r.count > 0 ? r.totalVolume / r.count : 0;
        return { ...r, conversion, avgVolumePerDossier };
      })
      .sort(
        (a, b) =>
          b.dealVolume - a.dealVolume ||
          b.totalVolume - a.totalVolume ||
          b.count - a.count,
      );
  }, [data]);

  if (loading) {
    return (
      <Card className="bg-white">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-2">
            <MapTrifold className="h-5 w-5 text-moss-primary" weight="duotone" />
            <Skeleton className="h-5 w-48" />
          </div>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[260px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-white">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <MapTrifold className="h-5 w-5 text-moss-primary" weight="duotone" />
          <CardTitle className="text-lg">Performance per provincie</CardTitle>
          <MetricInfo
            description={
              <div className="space-y-1">
                <p>
                  Provincie wordt afgeleid uit de Belgische postcode in het{' '}
                  <code>googlePlaceLongAddress</code> van de gekoppelde
                  gebouwen, met fallback op <code>City.province</code>.
                </p>
                <p>
                  <strong>Conversie:</strong> deals / (deals + afgewezen).
                </p>
                <p>
                  <strong>Volume:</strong> Σ{' '}
                  <code>(totalBid || bidPriceActual || grossAskingPrice)</code>;
                  evenredig verdeeld over provincies bij multi-regio dossiers.
                </p>
                <p>
                  <strong>Volume-gew. yield:</strong> Σ(yield × bod) / Σ(bod) op
                  deals — board-relevante metric want grote transacties wegen
                  zwaarder.
                </p>
              </div>
            }
          />
        </div>
        <p className="text-xs text-neutral-secondary-mid">
          Diepte-inzicht per regio: instroom, conversie, transactievolume en
          rendement
        </p>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <div className="flex h-[120px] items-center justify-center text-sm text-neutral-secondary-mid">
            Geen data beschikbaar
          </div>
        ) : (
          <div className="overflow-x-auto rounded-md border border-neutral-100">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50/70 text-xs uppercase tracking-wide text-neutral-secondary-mid">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Provincie</th>
                  <th className="px-3 py-2 text-right font-medium">Dossiers</th>
                  <th className="px-3 py-2 text-right font-medium">Deals</th>
                  <th className="px-3 py-2 text-right font-medium">Conversie</th>
                  <th className="px-3 py-2 text-right font-medium">Volume</th>
                  <th className="px-3 py-2 text-right font-medium">
                    Volume gewonnen
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    Gem. volume / dossier
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    Gem. yield
                  </th>
                  <th className="px-3 py-2 text-right font-medium">
                    Volume-gew. yield
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {rows.map((r) => (
                  <tr key={r.province} className="hover:bg-neutral-50/50">
                    <td className="px-3 py-2.5 font-medium text-neutral-primary">
                      {r.province}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.count}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <Badge
                        variant="secondary"
                        className="bg-moss-primary/10 text-moss-primary"
                      >
                        {r.deals}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.deals + r.rejected > 0
                        ? `${(r.conversion * 100).toFixed(0)}%`
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {formatCurrency(r.totalVolume)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-moss-primary">
                      {r.dealVolume > 0
                        ? formatCurrency(r.dealVolume)
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.avgVolumePerDossier > 0
                        ? formatCurrency(r.avgVolumePerDossier)
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.dealYieldSampleSize > 0
                        ? `${(r.avgDealYield * 100).toFixed(2)}%`
                        : '—'}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {r.dealYieldSampleSize > 0
                        ? `${(r.volumeWeightedDealYield * 100).toFixed(2)}%`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
